import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { isEmpty } from 'lodash';
import { useTracking } from '@podiumhq/tracking';

import { INIT_EMPTY_CC_FIELDS } from 'components/CreditCardDetails/constants';
import { getEmptyErrorMessage } from 'lib/fieldValidation';

export const CreditCardContext = createContext({});

export const useCreditCardContext = () => useContext(CreditCardContext);

// easily pass & update credit card info
const CreditCardContextProvider = ({
  children,
  ccFields = INIT_EMPTY_CC_FIELDS,
}) => {
  const { trackEvent } = useTracking();

  const [creditCardFields, setCreditCardFields] = useState(ccFields);
  const [zip, setZip] = useState('');

  const resetCreditCardErrors = useCallback(() => {
    const fields = Object.entries(creditCardFields).reduce(
      (fields, [key, valueObject]) => {
        fields[key] = {
          ...valueObject,
          error: null,
          shouldDisplayError: false,
        };
        return fields;
      },
      {}
    );
    setCreditCardFields(fields);
  }, [creditCardFields]);

  const hasValidCreditCard = useCallback(() => {
    const emptyFields = Object.entries(creditCardFields).reduce(
      (emptyFields, [key, valueObject]) => {
        if (!!valueObject?.isEmpty || !!valueObject?.isInvalid) {
          emptyFields[key] = {
            ...valueObject,
            shouldDisplayError: true,
          };
        }
        return emptyFields;
      },
      {}
    );
    setCreditCardFields(emptyFields);
    return isEmpty(emptyFields);
  }, [creditCardFields]);

  const validateCreditCardFields = useCallback(
    (trackingFieldErrorPath = 'trials.startTrial') => {
      const emptyFields = Object.entries(creditCardFields).reduce(
        (emptyFields, [key, valueObject]) => {
          const { error, isEmpty } = valueObject;
          if (error || !!isEmpty) {
            emptyFields[key] = {
              ...valueObject,
              error: error || getEmptyErrorMessage(key),
              shouldDisplayError: true,
            };
            trackingFieldErrorPath &&
              trackEvent(`${trackingFieldErrorPath}.fieldError`, {
                field: key,
              });
          }
          return emptyFields;
        },
        {}
      );
      setCreditCardFields(emptyFields);
      return isEmpty(emptyFields);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [creditCardFields]
  );

  const updateCreditCardField = useCallback(
    (fieldType, updatedValues) => {
      const updatedFields = {
        ...creditCardFields,
        [fieldType]: { ...updatedValues },
      };

      setCreditCardFields(updatedFields);
    },
    [creditCardFields]
  );

  const ccDetails = useMemo(
    () => ({
      creditCardFields,
      hasValidCreditCard,
      resetCreditCardErrors,
      setCreditCardFields,
      setZip,
      updateCreditCardField,
      validateCreditCardFields,
      zip,
    }),
    [
      creditCardFields,
      hasValidCreditCard,
      resetCreditCardErrors,
      updateCreditCardField,
      validateCreditCardFields,
      zip,
    ]
  );

  return (
    <CreditCardContext.Provider value={ccDetails}>
      {children}
    </CreditCardContext.Provider>
  );
};

export default CreditCardContextProvider;
